import { isDefined } from "@clipboard-health/util-ts";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import type { QueryClient } from "@tanstack/react-query";
import { addDays, endOfDay, min, parseISO, startOfDay } from "date-fns";
import { isEmpty } from "lodash";

import { resetGetOpenShifts } from "../../Shift/Open/useGetOpenShifts";
import { useGetOpenShiftsForDates } from "../../Shift/Open/useGetOpenShiftsForDates";
import {
  type OpenShiftDatesResponse,
  resetOpenShiftCount,
} from "../../Shift/Open/usePaginatedOpenShiftDates";
import { getShiftDiscoveryDefaultDateRange } from "../../utils/getShiftDiscoveryDefaultDateRange";
import { useShiftDiscoveryDaysToFetch } from "../../utils/useShiftDiscoveryDaysToFetch";
import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../Filters/constants";
import { useFavoriteWorkplacesOpenShiftsCount } from "./useFavoriteWorkplacesOpenShiftsCount";
import { useGetFavoriteWorkplaceEntitlement } from "./useGetFavoriteWorkplaceEntitlement";

function calculateInitialDateRange(
  favoriteWorkplacesOpenShiftsCountByDate: OpenShiftDatesResponse | undefined,
  daysToFetch: number
) {
  if (isEmpty(favoriteWorkplacesOpenShiftsCountByDate)) {
    return getShiftDiscoveryDefaultDateRange(daysToFetch);
  }

  const datesWithShifts = Object.keys(favoriteWorkplacesOpenShiftsCountByDate ?? {}).map((date) =>
    parseISO(date)
  );
  const startDate = min(datesWithShifts);
  return {
    startDate: startOfDay(startDate),
    endDate: endOfDay(addDays(startDate, daysToFetch)),
  };
}

export async function refetchFavoriteWorkplacesShiftsData(queryClient: QueryClient) {
  // We want loader state to be shown so we use reset instead of invalidate
  await Promise.all([resetOpenShiftCount(queryClient), resetGetOpenShifts(queryClient)]);
}

export function useFavoriteWorkplacesShiftsData() {
  const workerGeoLocation = useWorkerGeoLocation();

  const { isEntitlementEnabled: isFavoriteWorkplacesEnabled } =
    useGetFavoriteWorkplaceEntitlement();
  const { favoriteWorkplacesOpenShiftsCountByDate, status } =
    useFavoriteWorkplacesOpenShiftsCount();
  const daysToFetch = useShiftDiscoveryDaysToFetch();

  const { shifts, ...rest } = useGetOpenShiftsForDates({
    dateRange: calculateInitialDateRange(favoriteWorkplacesOpenShiftsCountByDate, daysToFetch),
    dates: [], // Product requirement: we don't want to filter by individual dates, or use global date filter user selects
    filter: {
      isFavoriteWorkplaceShift: true,
      area: {
        latitude: workerGeoLocation?.latitude ?? 0,
        longitude: workerGeoLocation?.longitude ?? 0,
        radiusInMiles: DEFAULT_DISTANCE_IN_MILES_FILTER,
      },
    },
    enabled: isDefined(workerGeoLocation) && isFavoriteWorkplacesEnabled && status === "success",
  });

  return {
    ...rest,
    shifts,
  };
}
